/**
 * Fabrique des blocs CMS avancé de la categorie "artworks" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { ExhibitionListPickerField } from "@/components/cms-puck/fields/ExhibitionListPickerField";
import { ItemSpotlightPickerField } from "@/components/cms-puck/fields/ItemSpotlightPickerField";
import { SelectionCategoryPreviewField } from "@/components/cms-puck/fields/SelectionCategoryPreviewField";
import { SelectionDisplayField } from "@/components/cms-puck/fields/SelectionDisplayField";
import { SelectionPickerField } from "@/components/cms-puck/fields/SelectionPickerField";
import { SelectionSectionsField } from "@/components/cms-puck/fields/SelectionSectionsField";
import {
  SelectionCategoryDescriptionPreview,
  SelectionCategoryDetailPreview,
  SelectionCategoryTitlePreview,
} from "@/components/cms-puck/previews/SelectionCategoryPreviews";
import {
  DEFAULT_SELECTION_VISIBILITY,
  normalizeSelectionCategories,
  normalizeSelectionSectionsMode,
  selectionCategoryPlainTitle,
  type SelectionCategory,
  type SelectionItem,
  type SelectionVisibility,
} from "@/lib/cms-puck/blocks/selection";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { ArtworkSelectionPreview, ExhibitionListPreview, ItemSpotlightPreview, type SampleArtworkCard } from "@/components/cms-puck/blocks/catalogue-previews";
import { MediaPreview } from "@/components/cms-puck/blocks/MediaPreview";
import { ColumnCountSliderField } from "@/components/cms-puck/fields/ColumnCountSliderField";
import {
  MAX_CONTENT_COLUMNS,
  selectionCellLayout,
  selectionColumnWidthPercent,
} from "@/lib/cms-puck/blocks/column-count";
import { type CustomField } from "@measured/puck";
import { type RawComponentsConfig } from "../types";

export function buildArtworksBlocks({
  contentColumnCountField,
}: {
  contentColumnCountField: CustomField<2 | 3 | 4>;
}) {
  return {
    SelectionCategoryTitle: {
      label: cmsNew(
        "cms.new.blocks.selectionCategoryTitle.label",
        "Titre de rubrique",
      ),
      fields: {
        level: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.niveau_structurel",
            "Niveau (structurel)",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.h1", "H1"), value: "h1" },
            { label: cmsNew("cms.new.auto.label.h2", "H2"), value: "h2" },
            { label: cmsNew("cms.new.auto.label.h3", "H3"), value: "h3" },
            { label: cmsNew("cms.new.auto.label.h4", "H4"), value: "h4" },
            { label: cmsNew("cms.new.auto.label.h5", "H5"), value: "h5" },
            { label: cmsNew("cms.new.auto.label.h6", "H6"), value: "h6" },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.info", "Info"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.selectionCategoryTitle",
                "Affiche le titre de la rubrique (route publique / aperçu éditeur). Aucun texte à saisir ici.",
              )}
            </p>
          ),
        },
      },
      defaultProps: {
        level: "h1" as const,
        align: "center" as const,
        info: null,
      },
      render: ({ level, align, ...rest }) => (
        <SelectionCategoryTitlePreview
          level={level}
          align={align}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    SelectionCategoryDescription: {
      label: cmsNew(
        "cms.new.blocks.selectionCategoryDescription.label",
        "Description de rubrique",
      ),
      fields: {
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.info", "Info"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.selectionCategoryDescription",
                "Affiche la description de la rubrique. Éditez-la dans le bloc « Sélection — catégories ».",
              )}
            </p>
          ),
        },
      },
      defaultProps: {
        align: "center" as const,
        info: null,
      },
      render: ({ align, ...rest }) => (
        <SelectionCategoryDescriptionPreview
          align={align}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    SelectionCategoryDetail: {
      label: cmsNew(
        "cms.new.blocks.selectionCategoryDetail.label",
        "Fiche rubrique (sélection)",
      ),
      fields: {
        previewCategoryId: {
          type: "custom",
          label: cmsNew(
            "cms.new.fields.selectionCategoryPreview.label",
            "Rubrique d'aperçu",
          ),
          render: ({ value, onChange }) => (
            <SelectionCategoryPreviewField
              value={value as string | null | undefined}
              onChange={onChange}
            />
          ),
        },
        columns: {
          type: "custom",
          label: cmsNew("cms.new.fields.columnCount.colonnes", "Colonnes"),
          render: ({ value, onChange }) => (
            <ColumnCountSliderField
              label={cmsNew("cms.new.fields.columnCount.colonnes", "Colonnes")}
              value={value}
              onChange={(next) => onChange(next as 2 | 3 | 4)}
              min={1}
              max={MAX_CONTENT_COLUMNS}
              fallback={4}
              persistFallback
            />
          ),
        },
        displayMode: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.mode_daffichage",
            "Mode d'affichage",
          ),
          options: [
            {
              label: cmsNew("cms.new.blocks.grid.label", "Grille"),
              value: "grid",
            },
            {
              label: cmsNew("cms.new.blocks.carousel.label", "Carrousel"),
              value: "carousel",
            },
          ],
        },
        alignment: {
          type: "select",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.aligne_par_le_centre",
                "Aligné par le centre",
              ),
              value: "center",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.aligne_par_le_haut",
                "Aligné par le haut",
              ),
              value: "top",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.aligne_sur_une_grille",
                "Aligné sur une grille",
              ),
              value: "grid",
            },
          ],
        },
        captionAlign: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.alignement_des_legendes",
            "Alignement des légendes",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        visibility: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.infos_affichees",
            "Infos affichées",
          ),
          render: ({ value, onChange }) => (
            <SelectionDisplayField
              value={value as SelectionVisibility | undefined}
              onChange={onChange}
            />
          ),
        },
      },
      defaultProps: {
        previewCategoryId: null as string | null,
        columns: 4 as const,
        displayMode: "grid" as const,
        alignment: "center" as const,
        captionAlign: "left" as const,
        visibility: { ...DEFAULT_SELECTION_VISIBILITY },
      },
      render: ({
        columns,
        displayMode,
        alignment,
        captionAlign,
        visibility,
        ...rest
      }) => (
        <SelectionCategoryDetailPreview
          columns={columns}
          displayMode={displayMode}
          alignment={alignment}
          captionAlign={captionAlign}
          visibility={visibility}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    SelectionSections: {
      label: cmsNew(
        "cms.new.blocks.selectionSections.label",
        "Sélection — catégories",
      ),
      // Éditeur custom unique : les autres props sont gérées dans
      // SelectionSectionsField (pas de champs Puck individuels).
      // `as any` : Fields<SelectionSections> exige une entrée par prop.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fields: {
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.selection", "Sélection"),
          render: () => <SelectionSectionsField />,
        },
      } as any,
      defaultProps: {
        mode: "categorized" as const,
        categoryColumns: 4 as const,
        categoryAlignment: "grid" as const,
        categoryCaptionAlign: "left" as const,
        categories: [] as SelectionCategory[],
        items: [] as SelectionItem[],
        columns: 4 as const,
        alignment: "center" as const,
        captionAlign: "left" as const,
        visibility: { ...DEFAULT_SELECTION_VISIBILITY },
        info: null,
      },
      render: ({
        mode,
        categoryColumns,
        categories,
        items,
        columns,
        alignment,
        captionAlign,
        visibility,
        ...rest
      }) => {
        const resolvedMode = normalizeSelectionSectionsMode(mode);
        const sampleCategories =
          (
            rest as {
              sampleCategories?: {
                title: string;
                description: string;
                imageUrl?: string;
              }[];
            }
          ).sampleCategories ?? [];
        const cats =
          normalizeSelectionCategories(categories).length > 0
            ? normalizeSelectionCategories(categories)
            : sampleCategories.map((s, i) => ({
                id: `sample-${i}`,
                title: s.title,
                description: s.description,
                thumbnailAttachmentId: null,
                items: [],
                columns: 4 as const,
                alignment: "center" as const,
                captionAlign: "left" as const,
                visibility: { ...DEFAULT_SELECTION_VISIBILITY },
                _imageUrl: s.imageUrl,
              }));

        if (resolvedMode === "global") {
          return (
            <ArtworkSelectionPreview
              items={items}
              columns={columns}
              alignment={alignment}
              captionAlign={captionAlign}
              visibility={visibility}
              sampleCards={
                (rest as { sampleCards?: SampleArtworkCard[] }).sampleCards
              }
              style={(rest as { style?: BlockStyle }).style}
            />
          );
        }

        const cell = selectionCellLayout(
          selectionColumnWidthPercent(categoryColumns ?? 4),
        );
        return (
          <div className="w-full">
            {cats.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">
                {cmsNew(
                  "cms.new.hint.selectionSections.emptyCanvas",
                  "Ajoutez des rubriques dans le panneau de droite.",
                )}
              </p>
            ) : (
              // Parité front (`CategoryCard`) : cartes en flex-wrap, pleine
              // largeur en mobile, largeur de colonne au-delà.
              <div className="flex w-full flex-wrap gap-8">
                {cats.map((cat) => {
                  const sampleUrl =
                    "_imageUrl" in cat &&
                    typeof (cat as { _imageUrl?: string })._imageUrl === "string"
                      ? (cat as { _imageUrl?: string })._imageUrl
                      : undefined;
                  const thumbId = cat.thumbnailAttachmentId?.trim() || undefined;
                  return (
                  <div
                    key={cat.id}
                    className={`flex flex-col gap-2 ${cell.className}`}
                    style={cell.style}
                  >
                    <div className="aspect-square w-full overflow-hidden rounded bg-slate-100">
                      {thumbId || sampleUrl ? (
                        <MediaPreview
                          attachmentId={thumbId}
                          sampleUrl={sampleUrl}
                          size="medium"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    {cat.title?.trim() ? (
                      <div
                        className="cms-html-content text-slate-700 [&_p]:m-0"
                        dangerouslySetInnerHTML={{ __html: cat.title }}
                      />
                    ) : (
                      <span className="text-sm font-semibold text-slate-700">
                        {selectionCategoryPlainTitle(cat.title) ||
                          cmsNew(
                            "cms.new.preview.selectionSections.category",
                            "Catégorie",
                          )}
                      </span>
                    )}
                    {cat.description?.trim() ? (
                      <span
                        className="line-clamp-3 text-xs text-slate-500"
                        dangerouslySetInnerHTML={{
                          __html: cat.description,
                        }}
                      />
                    ) : (
                      <span className="text-xs text-slate-400">
                        {cmsNew(
                          "cms.new.auto.text.description",
                          "Description…",
                        )}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400">
                      {cat.items?.length ?? 0}{" "}
                      {cmsNew("cms.new.auto.label.items", "élément(s)")}
                    </span>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      },
    },

    ArtworkSelection: {
      label: cmsNew(
        "cms.new.blocks.artworkSelection.label",
        "Sélection d'œuvres",
      ),
      fields: {
        // Sélection STRUCTURELLE (œuvres + multiples, partagée toutes langues).
        items: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.selection", "Sélection"),
          render: ({ value, onChange }) => (
            <SelectionPickerField
              value={value as SelectionItem[] | undefined}
              onChange={onChange}
            />
          ),
        },
        columns: contentColumnCountField,
        displayMode: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.mode_daffichage",
            "Mode d'affichage",
          ),
          options: [
            {
              label: cmsNew("cms.new.blocks.grid.label", "Grille"),
              value: "grid",
            },
            {
              label: cmsNew("cms.new.blocks.carousel.label", "Carrousel"),
              value: "carousel",
            },
          ],
        },
        // Alignement (façon ancien CMS) : images en taille naturelle ; on choisit
        // leur disposition. Pas de recadrage/carré.
        alignment: {
          type: "select",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.aligne_par_le_centre",
                "Aligné par le centre",
              ),
              value: "center",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.aligne_par_le_haut",
                "Aligné par le haut",
              ),
              value: "top",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.aligne_sur_une_grille",
                "Aligné sur une grille",
              ),
              value: "grid",
            },
          ],
        },
        captionAlign: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.alignement_des_legendes",
            "Alignement des légendes",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        visibility: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.infos_affichees",
            "Infos affichées",
          ),
          render: ({ value, onChange }) => (
            <SelectionDisplayField
              value={value as SelectionVisibility | undefined}
              onChange={onChange}
            />
          ),
        },
      },
      defaultProps: {
        items: [],
        columns: 3,
        displayMode: "grid",
        alignment: "grid",
        captionAlign: "left",
        visibility: { ...DEFAULT_SELECTION_VISIBILITY },
      },
      render: ({
        items,
        columns,
        alignment,
        displayMode,
        captionAlign,
        visibility,
        ...rest
      }) => (
        <ArtworkSelectionPreview
          items={items}
          artworkIds={(rest as { artworkIds?: string }).artworkIds}
          columns={columns}
          alignment={alignment}
          displayMode={displayMode}
          captionAlign={captionAlign}
          visibility={visibility}
          sampleCards={
            (rest as { sampleCards?: SampleArtworkCard[] }).sampleCards
          }
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    ItemSpotlight: {
      label: cmsNew(
        "cms.new.auto.label.element_en_vedette",
        "Élément en vedette",
      ),
      fields: {
        // Sélection STRUCTURELLE d'UN élément (toutes tables d'œuvres).
        item: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.element", "Élément"),
          render: ({ value, onChange }) => (
            <ItemSpotlightPickerField
              value={value as SelectionItem | undefined}
              onChange={onChange}
            />
          ),
        },
        captionAlign: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.alignement_de_la_legende",
            "Alignement de la légende",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        visibility: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.infos_affichees",
            "Infos affichées",
          ),
          render: ({ value, onChange }) => (
            <SelectionDisplayField
              value={value as SelectionVisibility | undefined}
              onChange={onChange}
            />
          ),
        },
      },
      defaultProps: {
        item: null,
        captionAlign: "left",
        visibility: { ...DEFAULT_SELECTION_VISIBILITY },
      },
      render: ({ item, captionAlign, visibility, ...rest }) => (
        <ItemSpotlightPreview
          item={item}
          captionAlign={captionAlign}
          visibility={visibility}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    ExhibitionList: {
      label: cmsNew(
        "cms.new.blocks.exhibitionList.label",
        "Liste d'expositions",
      ),
      fields: {
        items: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.expositions", "Expositions"),
          render: ({ value, onChange }) => (
            <ExhibitionListPickerField
              value={value as SelectionItem[] | undefined}
              onChange={onChange}
            />
          ),
        },
        columns: contentColumnCountField,
        captionAlign: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
      },
      defaultProps: {
        items: [],
        columns: 3,
        captionAlign: "left",
      },
      render: ({ items, columns, captionAlign, ...rest }) => (
        <ExhibitionListPreview
          items={items}
          columns={columns}
          captionAlign={captionAlign}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },
  } as Pick<
    RawComponentsConfig,
    | "SelectionCategoryTitle"
    | "SelectionCategoryDescription"
    | "SelectionCategoryDetail"
    | "SelectionSections"
    | "ArtworkSelection"
    | "ItemSpotlight"
    | "ExhibitionList"
  >;
}
